import type { GenericEnumObject } from '~/types/objects'
import { useContentSerializer } from '~~/shared-components/serializers/content'
import { locales } from '@@/shared-components/locales/index'

export const infiniteArticlesQuery = ({
  query,
  thumbs,
  hasPreview,
  placeholderData,
  forceData,
  dataLang,
}: {
  query?: Ref<object>
  thumbs?: {
    [key: string]: object
  }
  hasPreview?: { expires: string; signature: string }
  placeholderData?: ArticlesDataList
  forceData?: boolean
  dataLang: Ref<ValueOf<typeof locales>>
}) =>
  infiniteQueryOptions({
    queryKey: ['articles', 'list', dataLang, query],
    queryFn: async ({ pageParam }): Promise<ArticlesDataList> => {
      // Nefetchuje z BE pokud již data má ale queryClient není k dispozici (např. v middleweru)
      if (forceData && placeholderData) {
        return placeholderData
      }
      return await useCustomFetch<ArticlesDataList>({
        url: '/public/articles',
        query: {
          ...query?.value,
          page: {
            ...(query?.value && 'page' in query.value && typeof query.value.page === 'object'
              ? query.value.page
              : {}),
            number: pageParam,
          },
          ...(hasPreview ?? {}),
        },
        clientOnly: true,
        thumbs,
      })
    },
    initialPageParam: 1,
    getNextPageParam: (currentPage) => {
      if (currentPage.meta?.current_page === currentPage.meta.last_page) {
        return undefined
      }
      return currentPage.meta.current_page + 1
    },
    getPreviousPageParam: (currentPage) => {
      if (currentPage.meta?.current_page === 1) {
        return undefined
      }
      return currentPage.meta?.current_page - 1
    },
  })

export const articleKeysQuery = ({ query }: { query?: Ref<object> } = {}) =>
  queryOptions({
    queryKey: ['article-keys', query],
    queryFn: async ({ signal }): Promise<GenericEnumObject[]> => {
      const { get } = useApi()
      const res = await get<GenericEnumObject[]>(
        '/enums/article-keys',
        {
          ...query?.value,
        },
        { signal }
      )
      return res.data
    },
  })

export const articleQuery = ({
  articleName,
  query,
  thumbs,
  hasPreview,
  placeholderData = (v) => v!,
  forceData = false,
  enabled = shallowRef(true),
  throwOnError = true,
  throw404 = true,
  origin,
  dataLang,
}: {
  articleName?: string | Ref<string | undefined>
  query?: object
  thumbs?: {
    [key: string]: object
  }
  hasPreview?: { expires: string; signature: string }
  placeholderData?: Ref<Article> | ((data?: Article) => Article)
  enabled?: Ref<boolean>
  throwOnError?: boolean
  throw404?: boolean
  origin?: string
  dataLang: Ref<ValueOf<typeof locales>>
}) =>
  queryOptions({
    enabled,
    retry: 0,
    throwOnError,
    queryKey: ['articles', 'detail', articleName, dataLang, query],
    queryFn: async (): Promise<Article | null> => {
      try {
        const { deserialize } = useContentSerializer()
        const article = unref(articleName)

        const isArticleNameKey = Number.isNaN(parseInt(article!))

        const url = isArticleNameKey
          ? `/public/articles/${hasPreview ? 'p/keys/' : ''}${article}`
          : `/public/articles/${hasPreview ? 'p/' : ''}${article}`

        const res = await useCustomFetch<{ data: Article }>({
          url,
          query: { ...query, ...(hasPreview ?? {}) },
          thumbs,
          clientOnly: true,
          throw404,
          options: { headers: { 'X-Lang': dataLang.value, 'X-Data-Lang': dataLang.value } },
          origin,
        })
        if (res.data) {
          return deserialize(res?.data) as Article
        }
        return null
      } catch (e) {
        console.log(e)
        console.log('ERRRRR')
        throw e
      }
    },
    placeholderData,
  })
